// =============================================================================
// MeuDriver — Booking Flow shared atoms (StepIndicator + InstructorSummary)
// =============================================================================

import { Check, Star } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { formatCurrency, getInitials, formatRating } from '@/lib/utils'
import type { InstructorWithProfile } from '@/types/database'

export type Step = 1 | 2 | 3

const STEPS = [
  { num: 1 as Step, label: 'Data e horário' },
  { num: 2 as Step, label: 'Local' },
  { num: 3 as Step, label: 'Confirmar' },
]

export function StepIndicator({ currentStep }: { currentStep: Step }) {
  return (
    <div className="flex items-center justify-between">
      {STEPS.map((step, idx) => {
        const isDone = step.num < currentStep
        const isActive = step.num === currentStep
        return (
          <div key={step.num} className="flex flex-1 items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors
                  ${isDone ? 'bg-green-500 text-white' : isActive ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'}`}
              >
                {isDone ? <Check className="h-4 w-4" /> : step.num}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 rounded transition-colors ${isDone ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

export function InstructorSummaryCard({ instructor }: { instructor: InstructorWithProfile }) {
  const name = instructor.profile?.full_name ?? 'Instrutor'
  const avatar = instructor.profile?.avatar_url ?? undefined

  return (
    <Card className="mb-6">
      <CardContent className="p-4 flex items-center gap-4">
        <Avatar className="h-14 w-14">
          <AvatarImage src={avatar} />
          <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{name}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <Star className="h-3.5 w-3.5 text-orange-400 fill-current" />
            <span className="text-sm text-gray-600">{formatRating(instructor.rating_avg)}</span>
            <span className="text-xs text-muted-foreground ml-1">
              ({instructor.total_lessons} aulas)
            </span>
          </div>
          {instructor.hourly_rate != null && (
            <p className="text-sm font-medium text-primary mt-0.5">
              {formatCurrency(instructor.hourly_rate)}/h
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function RedirectingToPayment() {
  return (
    <div className="flex flex-col items-center py-12 gap-4 text-center">
      <div className="h-16 w-16 rounded-full bg-brand-100 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Redirecionando para o pagamento…</h2>
        <p className="text-muted-foreground text-sm">
          Você será direcionado ao Mercado Pago em instantes.
        </p>
      </div>
    </div>
  )
}

export interface AppliedCoupon {
  id: string
  code: string
  discount_type: 'percent' | 'fixed'
  discount_value: number
}

export interface AppliedGiftCard {
  gift_card_id: string
  code: string
  available_amount: number
  remaining: number
  expires_at: string
}
